import {
  LitElement,
  html,
  classMap,
  css,
  unsafeHTML,
  t,
  paths,
} from '@hh/global.js'

class HHWistiaVideoWrapper extends LitElement {
  static properties = {
    videoId: {
      type: String,
      reflect: true,
      attribute: 'video-id',
    },
    mobileVideoId: {
      type: String,
      reflect: true,
      attribute: 'mobile-video-id',
    },
    paused: {
      type: Boolean,
      reflect: true,
    },
    video: {
      type: Object,
    },
    mobileVideo: {
      type: Object,
    },
    isHero: {
      type: Boolean,
    },
    isCollection: {
      type: Boolean,
      reflect: true,
      attribute: 'is-collection',
    },
    onlyMobile: {
      type: Boolean,
      reflect: true,
      attribute: 'only-mobile',
    },
    onlyDesktop: {
      type: Boolean,
      reflect: true,
      attribute: 'only-desktop',
    },
    wistiaControls: {
      type: Boolean,
      reflect: true,
      attribute: 'wistia-controls',
    },
    forceVideoDimensions: {
      type: Boolean,
      attribute: 'force-video-dimensions',
    },
    muted: {
      type: Boolean,
      attribute: 'muted',
      default: false
    }
  }

  constructor() {
    super()
    window._wq = window._wq || []
    this.paused = true
    this.isMobile = matchMedia('(max-width: 749px)').matches
    this.isMotionReduced = matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    this.hasMobileVideo = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.wistiaOptions = {
      videoFoam: true,
      fitStrategy: 'cover',
      endVideoBehavior: 'loop',
      volume: 0,
      autoplay: !this.isMotionReduced,
      controlsVisibleOnLoad: this.wistiaControls,
      playSuspendedOffScreen: false,
      silentAutoplay: true,
      playPauseNotifier: false,
      muted: this.muted
    }

    window.addEventListener('resize', this.onResize.bind(this))
  }

  discountedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('resize', this.onResize)
  }

  firstUpdated() {
    this.containerHeight = this.getBoundingClientRect().height
    const self = this

    if (this.isMobile && this.mobileVideoId) {
      this.hasMobileVideo = true
      _wq.push({
        id: this.mobileVideoId,
        options: this.wistiaOptions,
        onReady: (video) => {
          self.mobileVideo = video
          self.initMobileVideo.bind(self)()
        },
      })
    }

    _wq.push({
      id: this.videoId,
      options: this.wistiaOptions,
      onReady: (video) => {
        self.video = video
        self.initVideo.bind(self)()
      },
    })
  }

  updated(changedProperties) {
    if (changedProperties.has('paused')) {
      if (this.paused) {
        this.pause()
      } else {
        this.play()
      }
    }
  }

  initVideo() {
    if (!this.video) return
    if (this.isMobile && this.onlyDesktop) {
      this.paused = true
      return
    }

    this.videoReady = true
    this.paused = this.isMotionReduced

    if (this.forceVideoDimensions) {
      this.video.videoHeight(this.containerHeight, { constrain: true })
    }
    this.video.volume(0)
  }

  initMobileVideo() {
    if (!this.mobileVideo) return

    this.mobileVideoReady = true
    this.paused = this.isMotionReduced

    if (this.forceVideoDimensions) {
      this.mobileVideo.videoHeight(this.containerHeight, { constrain: true })
    }
    this.mobileVideo.volume(0)
  }

  onResize() {
    this.containerHeight = this.getBoundingClientRect().height
    this.isMobile = matchMedia('(max-width: 749px)').matches

    if (this.isMobile && this.hasMobileVideo) {
      this.initMobileVideo()
      this.play()
    } else {
      this.initVideo()
    }
  }

  onToggleClick() {
    this.paused = !this.paused
  }

  play() {
    if (this.hasMobileVideo && this.isMobile) {
      this.mobileVideo?.play()
    } else {
      this.video?.play()
    }
  }

  pause() {
    if (this.hasMobileVideo && this.isMobile) {
      this.mobileVideo?.pause()
    } else {
      this.video?.pause()
    }
  }

  render() {
    return html`
      <div class="wrapper">
        <slot name="video"></slot>
        <slot name="mobile-video"></slot>
        <button
          class="${classMap({
      toggle: true,
      ready: this.videoReady,
      mobileReady: this.mobileVideoReady || this.hasMobileVideo == false,
      isCollection: this.isCollection,
      isHero: this.isHero,
      'use-wistia-controls': this.wistiaControls,
    })}"
          @click=${this.onToggleClick}
          aria-label=${this.paused
        ? t('accessibility.play')
        : t('accessibility.pause')}
        >
          ${this.paused ? unsafeHTML(paths.play) : unsafeHTML(paths.pause)}
        </button>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      height: 100%;
      background: var(--hh-color-foreground);
    }

    :host([only-desktop]) {
      display: none;
    }

    @media (min-width: 750px) {
      :host([only-desktop]) {
        display: block;
      }

      :host([only-mobile]) {
        display: none;
      }
    }

    .wrapper {
      height: 100%;
      position: relative;
    }

    .toggle {
      display: none;
      position: absolute;
      bottom: 2rem;
      right: 2rem;
      width: 4rem;
      height: 4rem;
      border-radius: 50%;
      border: none;
      color: var(--hh-color-background);
      background-color: var(--hh-color-day-extra-light-opacity);
      cursor: pointer;
      padding: 0;
      fill: currentColor;
      align-items: center;
      justify-content: center;
      z-index: 11;
    }

    .toggle.isCollection {
      bottom: 1rem;
      right: 1rem;
    }

    .toggle.mobileReady {
      display: flex;
    }

    .toggle.isHero {
      display: flex;
    }

    @media (min-width: 750px) {
      .toggle {
        bottom: var(--hh-toggle-bottom-offset, 5rem);
        right: 3rem;
      }

      .toggle.isCollection {
        bottom: 1rem;
        right: 1rem;
      }

      .toggle.ready {
        display: flex;
      }

      .toggle.isHero {
        display: flex;
      }
    }

    .toggle.use-wistia-controls {
      display: none;
    }

    .toggle svg {
      fill: transparent;
      transition: fill var(--hh-duration-default) ease-in-out;
    }

    @media (hover: hover) {
      .toggle:is(:focus-visible, :hover) svg {
        fill: currentColor;
      }
    }
  `
}

customElements.define('hh-wistia-video-wrapper', HHWistiaVideoWrapper)
